import { RSAA } from 'redux-api-middleware';
import moment from 'moment';
import uuid from 'node-uuid';
import { head, get, find, uniq } from 'lodash';
import endpoints from '../../utils/endpoints';
import { getTemplateFromPayments } from './templateRequestBuilder';
import {
	dateFormatYY,
	TYPE_FASTER_PAYMENTS,
	TYPE_BACS_CREDIT,
	TYPE_BACS_DEBIT,
	updateTemplateResponse,
	TYPE_CHAPS,
} from '../../config';

import { addNotification, clearAllNotification } from '../../states/notifications/actions';
import { fixAmount, getDate } from '../../helpers/fieldHelpers';
import { getCurrentTemplateId, getLastSaveType } from './selectors';
import {
	resetApp,
	goToPaymentsPage as goToBacsCreditPayments,
	goToDebitPaymentsPage,
	goToPayments,
	goToPage,
	pages,
	createNewBacsTemplate,
} from '../page/actions';
import { initialiseBacsCredit } from '../bacsCredit/actions';
import { initialiseBacsDebit } from '../bacsDebit/actions';
import { isBatchEligible } from '../payments/selectors';
import { toggleBatch } from '../batch/actions';
import { addValidationKey, removeKey, initValidationForTemplate } from '../validation/actions';
import { initialiseChaps } from '../chaps/actions';
import { getChapsReference, isBatchEligible as isChapsBatchEligible } from '../chaps/selectors';
import { getFirstSubmissionDate } from '../calendar/selectors';
import { getAccountById } from '../accounts/selectors';
import { getBeneficiaryByIdForTemplates } from '../commonSelectors/selectors';
import { getAccountDetailsById } from '../accounts/actions';
import { getBeneficiaryDetailsById } from '../payees/actions';
import { toggleTemplateMode } from './toggleTemplateMode';

export const SUBMIT_ALL_TEMPLATES_REQUEST = 'SUBMIT_ALL_TEMPLATES_REQUEST';
export const SUBMIT_ALL_TEMPLATES_SUCCESS = 'SUBMIT_ALL_TEMPLATES_SUCCESS';
export const SUBMIT_ALL_TEMPLATES_FAILURE = 'SUBMIT_ALL_TEMPLATES_FAILURE';

export const SUBMIT_GET_TEMPLATE_REQUEST = 'SUBMIT_GET_TEMPLATE_REQUEST';
export const SUBMIT_GET_TEMPLATE_SUCCESS = 'SUBMIT_GET_TEMPLATE_SUCCESS';
export const SUBMIT_GET_TEMPLATE_FAILURE = 'SUBMIT_GET_TEMPLATE_FAILURE';

export const SUBMIT_CREATE_TEMPLATE_REQUEST = 'SUBMIT_CREATE_TEMPLATE_REQUEST';
export const SUBMIT_CREATE_TEMPLATE_SUCCESS = 'SUBMIT_CREATE_TEMPLATE_SUCCESS';
export const SUBMIT_CREATE_TEMPLATE_FAILURE = 'SUBMIT_CREATE_TEMPLATE_FAILURE';

export const SUBMIT_UPDATE_TEMPLATE_REQUEST = 'SUBMIT_UPDATE_TEMPLATE_REQUEST';
export const SUBMIT_UPDATE_TEMPLATE_SUCCESS = 'SUBMIT_UPDATE_TEMPLATE_SUCCESS';
export const SUBMIT_UPDATE_TEMPLATE_FAILURE = 'SUBMIT_UPDATE_TEMPLATE_FAILURE';

export const SUBMIT_DELETE_TEMPLATE_REQUEST = 'SUBMIT_DELETE_TEMPLATE_REQUEST';
export const SUBMIT_DELETE_TEMPLATE_SUCCESS = 'SUBMIT_DELETE_TEMPLATE_SUCCESS';
export const SUBMIT_DELETE_TEMPLATE_FAILURE = 'SUBMIT_DELETE_TEMPLATE_FAILURE';

export const UPDATE_TEMPLATE_NAME = 'UPDATE_TEMPLATE_NAME';
export const RESET_CURRENT_TEMPLATE = 'RESET_CURRENT_TEMPLATE';
export const GO_TO_EDIT_FASTERPAYMENT_TEMPLATE = 'GO_TO_EDIT_FASTERPAYMENT_TEMPLATE';
export const GO_TO_BACS_TEMPLATE = 'GO_TO_BACS_TEMPLATE';

export const OVERWRITE_TEMPLATE_VERSION = 'OVERWRITE_TEMPLATE_VERSION';
export const LAST_TEMPLATE_SAVE_TYPE = 'LAST_TEMPLATE_SAVE_TYPE';

export const INIT_PAYMENTS_FOR_TEMPLATE = 'INIT_PAYMENTS_FOR_TEMPLATE';
export const INIT_PAYMENTS_FROM_TEMPLATE = 'INIT_PAYMENTS_FROM_TEMPLATE';

export const initialState = {
	currentTemplate: {
		name: `Template ${moment().format(dateFormatYY)}`,
		duplicateName: false,
		id: '',
		version: '',
		versionConflict: false,
		newVersion: '',
		updatedBy: '',
		lastUpdatedTime: '',
	},
	list: [],
	templateMode: false,
};

export const initialisePaymentsFromTemplate = (payments) => {
	const p = payments.map((payment) => ({ ...payment, date: getDate(payment) }));
	const actual = {
		type: INIT_PAYMENTS_FROM_TEMPLATE,
		payments: p,
	};
	return actual;
};

export const initialisePaymentsForTemplate = (payments) => {
	const p = payments.map((payment) => ({ ...payment, date: getDate(payment) }));
	const actual = {
		type: INIT_PAYMENTS_FOR_TEMPLATE,
		payments: p,
	};
	return actual;
};

export const resetCurrentTemplate = () => ({
	type: RESET_CURRENT_TEMPLATE,
	name: `Template ${moment().format(dateFormatYY)}`,
});

export const lastTemplateSaveType = (lastSaveType) => ({
	type: LAST_TEMPLATE_SAVE_TYPE,
	lastSaveType,
});

export const overwriteTemplateVersion = () => ({
	type: OVERWRITE_TEMPLATE_VERSION,
});

export const fetchTemplates = () => {
	const endpoint = endpoints.allTemplates;
	const action = {
		[RSAA]: {
			endpoint: endpoint.uri,
			method: endpoint.method,
			types: [SUBMIT_ALL_TEMPLATES_REQUEST, SUBMIT_ALL_TEMPLATES_SUCCESS, SUBMIT_ALL_TEMPLATES_FAILURE],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
		},
	};

	return action;
};

export const fetchTemplateById = (id) => {
	const endpoint = endpoints.getTemplate;
	const uri = endpoint.resolve(null, id);
	const action = {
		[RSAA]: {
			endpoint: uri,
			method: endpoint.method,
			types: [SUBMIT_GET_TEMPLATE_REQUEST, SUBMIT_GET_TEMPLATE_SUCCESS, SUBMIT_GET_TEMPLATE_FAILURE],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
		},
	};

	return action;
};

export const createTemplate = (template) => {
	const body = JSON.stringify(template);
	const endpoint = endpoints.createTemplate;
	const action = {
		[RSAA]: {
			endpoint: endpoint.uri,
			method: endpoint.method,
			types: [SUBMIT_CREATE_TEMPLATE_REQUEST, SUBMIT_CREATE_TEMPLATE_SUCCESS, SUBMIT_CREATE_TEMPLATE_FAILURE],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
			body,
		},
	};

	return action;
};

export const updateTemplate = (template) => {
	const body = JSON.stringify(template);
	const endpoint = endpoints.updateTemplate;
	const uri = endpoint.resolve(null, get(template, 'template.id'));
	const action = {
		[RSAA]: {
			endpoint: uri,
			method: endpoint.method,
			types: [SUBMIT_UPDATE_TEMPLATE_REQUEST, SUBMIT_UPDATE_TEMPLATE_SUCCESS, SUBMIT_UPDATE_TEMPLATE_FAILURE],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
			body,
		},
	};

	return action;
};

export const deleteTemplate = (templateId) => {
	const endpoint = endpoints.deleteTemplate;
	const uri = endpoint.resolve(null, templateId);
	const action = {
		[RSAA]: {
			endpoint: uri,
			method: endpoint.method,
			types: [SUBMIT_DELETE_TEMPLATE_REQUEST, SUBMIT_DELETE_TEMPLATE_SUCCESS, SUBMIT_DELETE_TEMPLATE_FAILURE],
			headers: { 'x-bpi-version': endpoint.x_bpi_version },
		},
	};

	return action;
};

export const addTemplateNameValidation = () => addValidationKey('template-name', { name: { ...initialState } });
export const removeTemplateNameValidation = () => removeKey({ id: 'template-name' });

export const submitDeleteTemplate = () => (dispatch, getState) => {
	const state = getState();
	const templateId = getCurrentTemplateId(state);
	return dispatch(deleteTemplate(templateId)).then((response) => {
		if (response.type === SUBMIT_DELETE_TEMPLATE_SUCCESS) {
			dispatch(resetApp);
		}
		return response;
	});
};

export const updateTemplateName = (name) => ({
	type: UPDATE_TEMPLATE_NAME,
	name,
});

export const goToBacsTemplate = (bacsType) => ({
	type: GO_TO_BACS_TEMPLATE,
	bacsType,
});
export const getBeneficiaryID = (beneficiaryID, state) => get(find(state.payees, { id: beneficiaryID }), 'id', '');

export const getAccountID = (accountId, state) => get(find(state.accounts, { id: accountId }), 'id', '');

const getFasterPayments = (template, state) =>
	template.entries.map(({ debit = {}, credit = {}, amount = {}, reference = '' }) => {
		const chapsReference = getChapsReference(state);
		return {
			id: uuid.v1(),
			fromAccount: debit.account || debit.beneficiary,
			toAccount: getAccountID(credit.account, state) || getBeneficiaryID(credit.beneficiary, state),
			date: moment(),
			type: 'single',
			amount: fixAmount(amount.value),
			reference,
			chapsReference,
		};
	});

const getBacsCreditPayments = (template, state) => {
	const bacsDetails = { fromAccount: head(template.entries).debit.account, bacsId: template.bacsId };
	const payments = template.entries.map(({ credit = {}, amount = {}, reference = '', hmrc_reference = '' }) => ({
		id: uuid.v1(),
		toAccount: credit.account || getBeneficiaryID(credit.beneficiary, state),
		amount: fixAmount(amount.value),
		reference,
		hmrcReference: hmrc_reference,
	}));

	return { bacsDetails, payments };
};

const getBacsDebitPayments = (template, state) => {
	const bacsDetails = { toAccount: head(template.entries).credit.account, bacsId: template.bacsId };
	const payments = template.entries.map(({ debit = {}, amount = {}, reference = '', transaction_code = '' }) => ({
		id: uuid.v1(),
		fromAccount: debit.account || getBeneficiaryID(debit.beneficiary, state),
		amount: fixAmount(amount.value),
		reference,
		transactionCode: transaction_code,
	}));

	return { bacsDetails, payments };
};

export const getPaymentsFromTemplate = (template, type, state) => {
	switch (type) {
		case TYPE_FASTER_PAYMENTS:
			return getFasterPayments(template, state);
		case TYPE_BACS_CREDIT:
			return getBacsCreditPayments(template, state);
		case TYPE_BACS_DEBIT: {
			return getBacsDebitPayments(template, state);
		}
		case TYPE_CHAPS:
			return getFasterPayments(template, state);
		default:
			return [];
	}
};

export const savePaymentsAsTemplate = (type) => (dispatch, getState) => {
	const state = getState();
	const templateDetails = getTemplateFromPayments(state, type);

	if (get(templateDetails, 'template.id')) return dispatch(updateTemplate(templateDetails));
	return dispatch(createTemplate(templateDetails));
};

export const savePaymentsAsTemplateAndNotify = (type) => (dispatch) =>
	dispatch(savePaymentsAsTemplate(type)).then((response) => {
		const failureType = get(response, 'payload.response.type', '');

		if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS) {
			dispatch(clearAllNotification());
			dispatch(addNotification('templateCreatedAndSaved'));
		}
		if (response.type === SUBMIT_UPDATE_TEMPLATE_SUCCESS) {
			dispatch(clearAllNotification());
			dispatch(addNotification('templateUpdated'));
		}

		if (response.type === SUBMIT_UPDATE_TEMPLATE_FAILURE && failureType === updateTemplateResponse.newVersion) {
			dispatch(lastTemplateSaveType('Save'));
		}
		return response;
	});

export const savePaymentsAsTemplateAndMakePayment = (type) => (dispatch, getState) =>
	dispatch(savePaymentsAsTemplate(type)).then((response) => {
		if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS || response.type === SUBMIT_UPDATE_TEMPLATE_SUCCESS) {
			if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS) {
				dispatch(clearAllNotification());
				dispatch(addNotification('templateCreatedAndSaved'));
			}
			if (response.type === SUBMIT_UPDATE_TEMPLATE_SUCCESS) {
				dispatch(clearAllNotification());
				dispatch(addNotification('templateUpdated'));
			}

			const { template } = response.payload;
			const state = getState();

			switch (type) {
				case TYPE_FASTER_PAYMENTS: {
					const payments = getPaymentsFromTemplate(template, TYPE_FASTER_PAYMENTS, state);
					dispatch(initialisePaymentsFromTemplate(payments));
					dispatch(toggleBatch(isBatchEligible(getState())));
					dispatch(toggleTemplateMode(false));
					dispatch(goToPayments());
					break;
				}
				case TYPE_BACS_CREDIT: {
					const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_CREDIT, state);
					dispatch(initialiseBacsCredit(bacsDetails, payments));
					dispatch(toggleTemplateMode(false));
					dispatch(goToBacsCreditPayments());
					break;
				}
				case TYPE_BACS_DEBIT: {
					const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_DEBIT, state);
					dispatch(initialiseBacsDebit(bacsDetails, payments));
					dispatch(toggleTemplateMode(false));
					dispatch(goToDebitPaymentsPage());
					break;
				}
				case TYPE_CHAPS: {
					const payments = getPaymentsFromTemplate(template, TYPE_CHAPS, state);
					dispatch(initValidationForTemplate());
					dispatch(initialiseChaps({ payments, date: getFirstSubmissionDate(state) }));
					dispatch(toggleTemplateMode(false));
					dispatch(toggleBatch(isChapsBatchEligible(getState())));
					dispatch(goToPage(pages.ChapsPayments));
					break;
				}
				default:
					break;
			}
		}
		const failureType = get(response, 'payload.response.type', '');
		if (response.type === SUBMIT_UPDATE_TEMPLATE_FAILURE && failureType === updateTemplateResponse.newVersion) {
			dispatch(lastTemplateSaveType('SaveAndMakePayment'));
		}
	});

export const savePaymentsAsTemplateAndExit = (type) => (dispatch) =>
	dispatch(savePaymentsAsTemplate(type)).then((response) => {
		if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS) {
			dispatch(clearAllNotification());
			dispatch(addNotification('templateSavedExit'));
			dispatch(resetApp);
		}
		if (response.type === SUBMIT_UPDATE_TEMPLATE_SUCCESS) {
			dispatch(clearAllNotification());
			dispatch(addNotification('templateUpdated'));
			dispatch(resetApp);
		}
		const failureType = get(response, 'payload.response.type', '');
		if (response.type === SUBMIT_UPDATE_TEMPLATE_FAILURE && failureType === updateTemplateResponse.newVersion) {
			dispatch(lastTemplateSaveType('SaveAndExit'));
		}
	});

export const getMissingAccountAndBeneficiaryDetails =
	({ entries = [] }) =>
	(dispatch, getState) => {
		const state = getState();
		const missingIds = entries.reduce(
			(missingList, entry) => {
				const missingAccountIds = [get(entry, 'credit.account'), get(entry, 'debit.account')];
				const missingBeneficiaryIds = [get(entry, 'credit.beneficiary'), get(entry, 'debit.beneficiary')];

				const filteredAccountsIdsFromState = missingAccountIds.filter(
					(accountId) => accountId && !getAccountById(state, accountId)
				);
				const filteredBeneficiaryIdsFromState = missingBeneficiaryIds.filter(
					(benId) => benId && !getBeneficiaryByIdForTemplates(state, benId)
				);

				if (filteredAccountsIdsFromState.length) {
					missingList.accounts.push(...filteredAccountsIdsFromState);
				}
				if (filteredBeneficiaryIdsFromState.length) {
					missingList.beneficiaries.push(...filteredBeneficiaryIdsFromState);
				}
				return missingList;
			},
			{ accounts: [], beneficiaries: [] }
		);
		return Promise.all([
			...uniq(missingIds.accounts).map((accountId) => dispatch(getAccountDetailsById(accountId))),
			...uniq(missingIds.beneficiaries).map((beneficiaryId) => dispatch(getBeneficiaryDetailsById(beneficiaryId))),
		]);
	};

export const selectPaymentTemplate = (id) => (dispatch, getState) =>
	dispatch(fetchTemplateById(id)).then((response) => {
		if (response.type === SUBMIT_GET_TEMPLATE_SUCCESS) {
			const { template } = response.payload;
			dispatch(getMissingAccountAndBeneficiaryDetails(template)).then(() => {
				const state = getState();

				switch (template.type) {
					case TYPE_FASTER_PAYMENTS: {
						const payments = getPaymentsFromTemplate(template, TYPE_FASTER_PAYMENTS, state);
						dispatch(initialisePaymentsFromTemplate(payments));
						dispatch(toggleBatch(isBatchEligible(getState())));
						dispatch(toggleTemplateMode(false));
						dispatch(goToPayments());
						break;
					}
					case TYPE_BACS_CREDIT: {
						const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_CREDIT, state);

						dispatch(initialiseBacsCredit(bacsDetails, payments));
						dispatch(toggleTemplateMode(false));
						dispatch(goToBacsCreditPayments());
						break;
					}
					case TYPE_BACS_DEBIT: {
						const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_DEBIT, state);

						dispatch(initialiseBacsDebit(bacsDetails, payments));
						dispatch(toggleTemplateMode(false));
						dispatch(goToDebitPaymentsPage());
						break;
					}
					case TYPE_CHAPS: {
						const payments = getPaymentsFromTemplate(template, TYPE_CHAPS, state);
						dispatch(initValidationForTemplate());
						dispatch(initialiseChaps({ payments, date: getFirstSubmissionDate(state) }));
						dispatch(toggleTemplateMode(false));
						dispatch(toggleBatch(isChapsBatchEligible(getState())));
						dispatch(goToPage(pages.ChapsPayments));
						break;
					}
					default:
						break;
				}
			});
		}
	});

export const editPaymentTemplate = (id) => (dispatch, getState) =>
	dispatch(fetchTemplateById(id)).then((response) => {
		if (response.type === SUBMIT_GET_TEMPLATE_SUCCESS) {
			const { template } = response.payload;
			dispatch(getMissingAccountAndBeneficiaryDetails(template)).then(() => {
				const state = getState();
				switch (template.type) {
					case TYPE_FASTER_PAYMENTS: {
						const payments = getPaymentsFromTemplate(template, TYPE_FASTER_PAYMENTS, state);
						dispatch(initValidationForTemplate());
						dispatch(initialisePaymentsForTemplate(payments));
						dispatch(toggleTemplateMode(true));
						dispatch(goToPage(pages.FasterPayments));
						break;
					}
					case TYPE_BACS_CREDIT: {
						const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_CREDIT, state);
						dispatch(initValidationForTemplate());
						dispatch(initialiseBacsCredit(bacsDetails, payments));
						dispatch(toggleTemplateMode(true));
						dispatch(goToPage(pages.BacsCreditPayments));
						break;
					}
					case TYPE_BACS_DEBIT: {
						const { payments, bacsDetails } = getPaymentsFromTemplate(template, TYPE_BACS_DEBIT, state);
						dispatch(initValidationForTemplate());
						dispatch(initialiseBacsDebit(bacsDetails, payments));
						dispatch(toggleTemplateMode(true));
						dispatch(goToPage(pages.BacsDebitPayments));
						break;
					}
					case TYPE_CHAPS: {
						const payments = getPaymentsFromTemplate(template, TYPE_CHAPS, state);
						dispatch(initValidationForTemplate());
						dispatch(initialiseChaps({ payments, date: getFirstSubmissionDate(state) }));
						dispatch(toggleTemplateMode(true));
						dispatch(goToPage(pages.ChapsPayments));
						break;
					}
					default:
						break;
				}
			});
		}
	});

export const overWriteTemplate = (type) => (dispatch, getState) => {
	dispatch(overwriteTemplateVersion());
	const state = getState();
	const lastSaveType = getLastSaveType(state);
	switch (lastSaveType) {
		case 'SaveAndExit':
			return dispatch(savePaymentsAsTemplateAndExit(type));
		case 'SaveAndMakePayment':
			return dispatch(savePaymentsAsTemplateAndMakePayment(type));
		default:
			return dispatch(savePaymentsAsTemplateAndNotify(type));
	}
};

export const createBacsDebitTemplate = () => (dispatch) => {
	dispatch(createNewBacsTemplate(TYPE_BACS_DEBIT));
	dispatch(toggleTemplateMode(true));
	return dispatch(goToPage(pages.BacsDebitPayments));
};

export const createBacsCreditTemplate = () => (dispatch) => {
	dispatch(createNewBacsTemplate(TYPE_BACS_CREDIT));
	dispatch(toggleTemplateMode(true));
	return dispatch(goToPage(pages.BacsCreditPayments));
};
